// Analytics adapter — GA4 sink (Google Analytics has NO BAA, so this path is
// strictly PHI-free).
//
// gtag is loaded lazily, once, and ONLY on the canonical hosted route (top-level
// window). When the widget is embedded in the Elementor iframe we do NOT load
// gtag: the host page's own tag consumes the coarse `expy:event` postMessages
// instead (see postMessage.ts), so a framed funnel is never double-counted here.
// Turn it on by setting VITE_GA4_MEASUREMENT_ID at build time; blank = no-op, so
// dev and staging stay out of the production property unless explicitly set.
//
// ⚠️ NO PHI. Two structural guards, not just a reminder:
//   1. GA4_ALLOWED — a param allowlist deliberately NARROWER than a BAA'd vendor's
//      would be: it drops sessionId, careType and free-text intent, keeping only
//      coarse, non-identifying keys (mirrors the registered custom dimensions).
//   2. scrubUrl — every URL handed to GA4 (page_location) has the `?prefill`
//      health token (body area + care type, see prefill.ts) and every bearer
//      credential in our links (claim/apply `token`, subscribe `st`, Stripe
//      `session_id`) removed. utm_*/gclid are preserved so attribution still works.
//   3. Google signals + ad personalization are OFF, so funnel hits are never joined
//      to a signed-in Google identity or used for ad targeting.
export type FunnelEvent =
  | 'landing_view'
  | 'cta_click'
  | 'questionnaire_start'
  | 'questionnaire_step' // a question was shown; funnel_step = stepKey() (health-neutral)
  | 'questionnaire_complete'
  | 'account_created'
  | 'trial_start' // fires in Phase 5 when Stripe lands
  // Subscription checkout (card-free trial model: checkout is optional, offered
  // after account creation and from trial-end nudges). Names only — no amounts.
  | 'checkout_start' // subscribe CTA clicked, redirecting to Stripe
  | 'checkout_completed' // /welcome verified the payment server-side
  | 'checkout_abandoned' // returned to /plan via Stripe's cancel URL
  | 'subscribe_link_open' // /subscribe landing (email token or universal URL)
  // Provider application funnel (/providers/apply). Names only — no PHI/PII.
  | 'provider_apply_view'
  | 'provider_apply_email_sent'
  | 'provider_apply_form_view'
  | 'provider_apply_photo_uploaded'
  | 'provider_apply_submitted'
  // Account claim (/claim). The ONLY event on that page — it handles a password, so
  // nothing else is instrumented there. `variant` is 'patient' | 'provider'.
  | 'claim_completed';

const GA4_ID = import.meta.env.VITE_GA4_MEASUREMENT_ID || '';

// Cross-domain: keep the marketing site and the funnel a single GA session so a
// click from expyhealth.com into the funnel is not counted as a new, self-referred
// session. (The marketing-site tag must list the same domains for full parity.)
const LINKER_DOMAINS = ['expyhealth.com', 'start.expyhealth.com'];

// The ONLY params allowed onto a GA4 event — narrower than a BAA'd vendor's would
// be. No sessionId, no careType, no free intent. Mirrors the registered custom
// dimensions (ref, funnel_step, variant) plus purchase economics (value, currency).
const GA4_ALLOWED = new Set(['ref', 'funnel_step', 'variant', 'value', 'currency']);

// Query params stripped from any URL sent to GA4. `prefill` encodes body area +
// care type — health context that must never reach Google. The rest are bearer
// credentials: anyone holding the URL can claim an account (/claim `token`), open a
// provider application (/providers/apply `token`), reach a checkout (/subscribe
// `st`) or read a Stripe session (/welcome `session_id`). `email` is defensive.
const URL_DENY = ['prefill', 'token', 'st', 'session_id', 'email'];

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

function framed(): boolean {
  try {
    return !!window.parent && window.parent !== window;
  } catch {
    return true; // cross-origin access threw → we are framed
  }
}

function scrubUrl(href: string): string {
  try {
    const u = new URL(href);
    for (const k of URL_DENY) u.searchParams.delete(k);
    return u.toString();
  } catch {
    return href;
  }
}

function pick(props: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(props)) {
    if (GA4_ALLOWED.has(k) && v !== undefined && v !== null && v !== '') out[k] = v;
  }
  return out;
}

let started = false;

/**
 * Load gtag once, on the canonical hosted route only (never framed). Idempotent
 * and safe to call on every boot/route. No-op when VITE_GA4_MEASUREMENT_ID is unset.
 */
export function initAnalytics(): void {
  if (started || !GA4_ID || typeof window === 'undefined' || framed()) return;
  started = true;
  window.dataLayer = window.dataLayer || [];
  // gtag.js only treats the `arguments` object as a command; a plain array push is
  // silently ignored (the library still loads, but no hit is ever sent). So this MUST
  // be a classic function pushing `arguments`, not an arrow that spreads into an array.
  function gtag() {
    // eslint-disable-next-line prefer-rest-params
    (window.dataLayer as unknown[]).push(arguments);
  }
  window.gtag = gtag as unknown as Gtag;
  window.gtag('js', new Date());
  // Hand GA4 a scrubbed location (no ?prefill health token); this call also emits
  // the initial page_view. utm_*/gclid survive the scrub, so acquisition is intact.
  window.gtag('config', GA4_ID, {
    linker: { domains: LINKER_DOMAINS },
    page_location: scrubUrl(window.location.href),
    // Same posture as expy-learn (Analytics.astro): no cross-device Google identity
    // join and no ad personalization off a health funnel.
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
  });
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA4_ID)}`;
  document.head.appendChild(s);
}

/**
 * Send a funnel event to GA4. No-op when GA4 is off or the widget is framed (the
 * host page's tag handles framed funnels via the postMessage bridge).
 */
export function track(event: FunnelEvent, props: Record<string, unknown> = {}): void {
  if (!GA4_ID || framed()) {
    if (import.meta.env.DEV) console.debug('[analytics:skip]', event, props);
    return;
  }
  initAnalytics();
  const clean = pick(props);
  window.gtag?.('event', event, clean);
  if (import.meta.env.DEV) console.debug('[analytics:ga4]', event, clean);
}

/**
 * GA4's recommended `purchase` event, fired alongside checkout_completed so Ads
 * can bid on revenue (ROAS). value/currency are the subscription price — not PHI.
 * Both optional: with no value it still counts as a conversion.
 */
export function trackPurchase(value?: number, currency?: string): void {
  if (!GA4_ID || framed()) return;
  initAnalytics();
  const params: Record<string, unknown> = {};
  if (typeof value === 'number' && value > 0) params.value = value;
  if (currency) params.currency = currency;
  window.gtag?.('event', 'purchase', params);
  if (import.meta.env.DEV) console.debug('[analytics:ga4] purchase', params);
}
